import { useMemo, useState } from 'react';
import {
  Badge,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Input,
  PageHeader,
  Toolbar,
  formatMoney,
  type BadgeTone,
} from '@biovet/ui';
import { useResource } from '../lib/api.js';
import { DateWindow, useDebounced } from './parts.js';
import { companyTimezone, companyToday } from '../lib/reference-data.js';


interface ActivityRow {
  at: string;
  who: string;
  action: string;
  entity: string;
  documentType: string | null;
  documentNo: string | null;
  amountBase: string | null;
}

const VERB: Record<string, string> = {
  INSERT: 'created',
  UPDATE: 'updated',
  DELETE: 'deleted',
  POST: 'posted',
  APPROVE: 'approved',
  REVERSE: 'reversed',
  CANCEL: 'cancelled',
  LOGIN: 'signed in',
  LOGOUT: 'signed out',
};

function noun(entity: string): string {
  return entity
    .replace(/^audit\./, '')
    .replace(/_/g, ' ')
    .replace(/s$/, '');
}

function toneOf(action: string): BadgeTone {
  if (action === 'POST' || action === 'APPROVE') return 'ok';
  if (action === 'DELETE' || action === 'CANCEL' || action === 'REVERSE') return 'bad';
  if (action === 'UPDATE') return 'warn';
  return 'mute';
}

function inCompanyZone(iso: string): { day: string; time: string } {
  const at = new Date(iso);
  const zone = companyTimezone();
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(at);
  const time = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(at);
  return { day: parts, time };
}

function dayLabel(day: string, today: string): string {
  if (day === today) return 'Today';
  const y = new Date(`${today}T00:00:00Z`);
  y.setUTCDate(y.getUTCDate() - 1);
  if (day === y.toISOString().slice(0, 10)) return 'Yesterday';
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${day}T00:00:00Z`));
}

export function RecentActivityScreen() {
  const [range, setRange] = useState({ from: '', to: '' });
  const [search, setSearch] = useState('');
  const settled = useDebounced(search);
  const { data, error, loading } = useResource<{ rows: ActivityRow[] }>('/recent-activity', {
    from: range.from || undefined,
    to: range.to || undefined,
    q: settled.trim() || undefined,
    limit: '200',
  });
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const today = companyToday();
  const narrowed = Boolean(range.from || range.to || settled.trim());

  const days = useMemo(() => {
    const out: Array<{ day: string; rows: Array<ActivityRow & { time: string }> }> = [];
    for (const r of rows) {
      const { day, time } = inCompanyZone(r.at);
      const last = out[out.length - 1];
      if (last && last.day === day) last.rows.push({ ...r, time });
      else out.push({ day, rows: [{ ...r, time }] });
    }
    return out;
  }, [rows]);

  return (
    <>
      <PageHeader
        breadcrumb="Dashboard / Recent Activity"
        title="Recent activity"
        description="What has been entered, posted and changed lately, newest first."
        actions={
          <Input
            size="sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Document no., user, type…"
            aria-label="Search recent activity"
            className="w-[240px]"
          />
        }
      />

      <Card flush className="mb-3.5" data-noprint="1">
        <Toolbar>
          <DateWindow range={range} onChange={setRange} label="All time" />
          <div className="flex-1" />
          {}
          <span className="text-[12px] text-[var(--ink-3)]">
            {rows.length} entr{rows.length === 1 ? 'y' : 'ies'}{' '}
            {range.from || range.to
              ? `dated ${range.from || 'the start of the book'} to ${range.to || 'today'}`
              : 'in the whole book'}
            {settled.trim() ? ` · matching “${settled.trim()}”` : ''}
          </span>
        </Toolbar>
      </Card>

      {error && (
        <Callout tone="danger" title="Could not read the activity log">
          {error}
        </Callout>
      )}

      {!error && !loading && rows.length === 0 && narrowed && (
        <Callout tone="info" title="Nothing matches">
          No document was entered, posted or changed in this window. Widen the dates, or clear the
          search.
        </Callout>
      )}

      {!error && !loading && rows.length === 0 && !narrowed && (
        <Callout tone="info" title="Nothing has happened yet">
          Every voucher, invoice and stock movement is recorded here as it is entered. This book has
          none yet.
        </Callout>
      )}

      {days.map((group) => (
        <Card key={group.day} flush className="mb-3.5">
          <CardHeader
            title={dayLabel(group.day, today)}
            subtitle={`${group.rows.length} entr${group.rows.length === 1 ? 'y' : 'ies'}`}
          />
          <CardBody className="flex flex-col gap-0">
            {group.rows.map((r, i) => (
              <div
                key={`${r.at}-${r.documentNo}-${i}`}
                className="flex items-baseline gap-3 border-b border-[var(--line)] py-2 last:border-0"
              >
                <span className="bb-mono w-[46px] shrink-0 text-[12.5px] text-[var(--ink-3)]">
                  {r.time}
                </span>
                <span className="w-[150px] shrink-0 truncate text-[13px]">{r.who}</span>
                <Badge tone={toneOf(r.action)}>{VERB[r.action] ?? r.action.toLowerCase()}</Badge>
                <span className="bb-mono shrink-0 text-[12.5px]">{r.documentNo}</span>
                <span className="flex-1 truncate text-[12.5px] text-[var(--ink-2)]">
                  {r.documentType ?? noun(r.entity)}
                </span>
                {}
                <span className="bb-mono shrink-0 text-[12.5px]">
                  {r.amountBase === null ? '' : formatMoney(r.amountBase, { decimals: 0 })}
                </span>
              </div>
            ))}
          </CardBody>
        </Card>
      ))}
    </>
  );
}
